import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  IFabricationContract,
  ILastOrderDetails
} from '../../../../../store/ducks/fabrication-contracts/types';
import moment from 'moment';
import OptionsColumn from './OptionsColumn';
import { Button } from '@gerdau/hefesto/Button';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import Table from '../../../../../components/Table';
import { ButtonWrapper, Container, OrderInfos } from './styles';
import { Tooltip } from '@gerdau/hefesto';

interface ConstructionIdentificationLastOrdersTableProps {
  selectedContract?: IFabricationContract;
  onBuyAgainClick(orderId: ILastOrderDetails): void;
  onRequestReview(contractOrder: string, orderId: number): void;
  revisionEnabled(orderId: number): {
    enableRevision: boolean;
    tooltipDisabledRevision: string;
  };
}

interface ConstructionIdentificationTableDataSource {
  key: number;
  orderNumber: string;
  sendDate: string;
  orderId: number;
  enableRevision?: boolean;
  formattedOrderCode?: string;
}

const ConstructionIdentificationLastOrdersTable: React.FC<
  ConstructionIdentificationLastOrdersTableProps
> = ({
  selectedContract,
  onBuyAgainClick,
  onRequestReview,
  revisionEnabled
}) => {
  const [constructionIdentificationData, setConstructionIdentificationData] =
    useState<ConstructionIdentificationTableDataSource[]>([]);

  const dispatch = useDispatch();
  const { location } = useHistory();

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    location.pathname,
    true
  );

  const columns = [
    {
      title: 'Número do pedido',
      dataIndex: 'formattedOrderCode',
      width: '10rem',
      sorter: (
        a: ConstructionIdentificationTableDataSource,
        b: ConstructionIdentificationTableDataSource
      ) =>
        a.orderNumber < b.orderNumber
          ? -1
          : a.orderNumber > b.orderNumber
          ? 1
          : 0,
      render: (text: string) => <>{text}</>
    },
    {
      title: 'Identificação do pedido',
      dataIndex: 'orderNumber',
      width: '10rem',
      sorter: (
        a: ConstructionIdentificationTableDataSource,
        b: ConstructionIdentificationTableDataSource
      ) =>
        a.orderNumber < b.orderNumber
          ? -1
          : a.orderNumber > b.orderNumber
          ? 1
          : 0,
      render: (text: string) => <>{text}</>
    },
    {
      title: 'Data de envio',
      dataIndex: 'sendDate',
      width: '7rem',
      sorter: (
        a: ConstructionIdentificationTableDataSource,
        b: ConstructionIdentificationTableDataSource
      ) =>
        moment(a.sendDate, 'YYYY-MM-DD').toDate().getTime() -
        moment(b.sendDate, 'YYYY-MM-DD').toDate().getTime(),
      render: (sendDate: string) => (
        <>{moment(sendDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}</>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '6rem',
      render: () => (
        <span
          style={{
            width: '9rem',
            backgroundColor: '#D1DFC4',
            color: '#3B5E2E',
            borderRadius: '80px',
            height: '1.5rem',
            display: 'block',
            textAlign: 'center'
          }}
        >
          Em implantação
        </span>
      )
    },
    {
      title: '',
      dataIndex: 'orderId',
      width: '6rem',
      render: (orderId: number) => (
        <OptionsColumn
          orderId={orderId}
          contractNumber={selectedContract?.contractNumber || ''}
        />
      )
    },
    {
      title: ' ',
      dataIndex: 'orderId',
      width: '6rem',
      render: (orderId: number) => (
        <Button
          type="button"
          name="button-on-by-again"
          variant="tertiary"
          onClick={() => {
            dispatch(
              sendTagging({
                screenName,
                itemName: 'button:recomprar_ultimo_pedido',
                event: 'click',
                filterInfo: `#cttObra:${selectedContract?.contractNumber}`,
                contratoObra: selectedContract?.contractNumber,
                itemDescription: `${itemDescription}_recomprarUltimoPedido`
              })
            );

            selectedContract &&
              selectedContract.lastOrderDetails &&
              onBuyAgainClick(
                selectedContract.lastOrderDetails.find(
                  (o) => o.id === orderId
                ) as ILastOrderDetails
              );
          }}
        >
          Recomprar pedido
        </Button>
      )
    },
    {
      title: ' ',
      dataIndex: 'orderId',
      width: '6rem',
      render: (orderId: number) => (
        <Tooltip
          {...{
            title: !revisionEnabled(orderId)?.enableRevision
              ? revisionEnabled(orderId)?.tooltipDisabledRevision
              : '',
            placement: 'bottom'
          }}
        >
          <ButtonWrapper disabled={!revisionEnabled(orderId)?.enableRevision}>
            <Button
              type="button"
              name="button-on-by-again"
              variant="tertiary"
              onClick={() => {
                if (!revisionEnabled(orderId)?.enableRevision) {
                  return null;
                }
                onRequestReview(
                  selectedContract?.contractNumber || '',
                  orderId
                );
                dispatch(
                  sendTagging({
                    screenName,
                    page: location.pathname,
                    itemName: 'button:recomprar',
                    itemDescription: `${itemDescription}_recomprar`,
                    event: 'click'
                  })
                );
              }}
            >
              Solicitar revisão
            </Button>
          </ButtonWrapper>
        </Tooltip>
      )
    }
  ];

  useEffect(() => {
    if (!selectedContract?.lastOrderDetails) {
      return;
    }

    const data: ConstructionIdentificationTableDataSource[] =
      selectedContract.lastOrderDetails?.map((order, idx) => {
        return {
          key: idx,
          codGerdau: order.contractNumber,
          orderNumber: order.orderNumber,
          sendDate: order.sendDate,
          orderId: order.id,
          enableRevision: order.enableRevision,
          formattedOrderCode: order.formattedOrderCode
        };
      });

    setConstructionIdentificationData(data);
  }, [selectedContract?.lastOrderDetails]);

  return (
    <>
      <OrderInfos>
        <p>
          Solicite revisões e alterações dos seus pedidos com pelo menos{' '}
          <span>7 dias úteis antes</span> da <span>data de entrega</span> para
          avaliação e confirmação da equipe de programação. Para{' '}
          <span>estaca armada</span> este prazo é de pelo menos{' '}
          <span>10 dias úteis.</span>
        </p>
      </OrderInfos>
      <Container>
        <Table
          columns={columns}
          data={constructionIdentificationData}
          scroll={{
            x: 'calc(15rem + 50%)'
          }}
          hasPagination
        />
      </Container>
    </>
  );
};

export default ConstructionIdentificationLastOrdersTable;
